import React, { useContext, useEffect, useRef } from "react";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";

import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { i18n } from "../../translate/i18n";
import { canManageConnections } from "../../helpers/menuAccess";
import {
  getAlertWhatsApps,
  isWhatsappOffline,
  isWhatsappOnline
} from "../../helpers/whatsappStatus";

// Carencia antes do primeiro aviso, contada desde a ultima vez que a conexao
// esteve no ar. O wbot reconecta sozinho com backoff (2,5s ate 5min), entao
// avisar antes disso geraria falso alarme para o usuario numa queda que o
// proprio sistema recupera. 15min cobre com folga a janela de auto-recuperacao.
const OFFLINE_GRACE_MS = 15 * 60 * 1000;
// Tick fixo: sustenta a carencia mesmo sem novos eventos de socket.
const TICK_MS = 15 * 1000;
const AUTO_CLOSE_MS = 10 * 1000;
// Sem clique no OK/X, o aviso fecha sozinho e conta como lido depois de 1min
// NA TELA. Com a aba em segundo plano a contagem pausa: dispensar ali seria
// marcar como lido um aviso que ninguem viu.
const AUTO_ACK_MS = 60 * 1000;
const MAX_NAMES = 3;
// O toast e sempre claro (ToastContainer sem `theme`). Cor fixa nos botoes em
// vez da do tema MUI: no tema escuro o "primary" e os icones sao brancos e o
// OK/X sumiam no fundo branco.
const TOAST_TEXT_COLOR = "var(--toastify-text-color-light)";

// Um unico toast de queda por vez: o conteudo e atualizado no lugar quando o
// conjunto de conexoes caidas muda, em vez de empilhar avisos novos.
const OFFLINE_TOAST_ID = "whatsapp-offline";
const onlineToastId = id => `whatsapp-online-${id}`;

// Dispensa persistida por usuario/empresa: id da conexao -> quando foi
// dispensada. Sobrevive a reload e a troca de tela, que era de onde vinha a
// repeticao incomoda do aviso.
const ackStorageKey = user =>
  `connectionOfflineAck:${user?.companyId || "default"}:${user?.id}`;

const readAck = key => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (err) {
    return {};
  }
};

const writeAck = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    // localStorage cheio/bloqueado nao pode derrubar o aviso.
  }
};

// Assinatura do que esta escrito no aviso aberto. Comparar so os ids nao basta:
// um texto desatualizado (conexao renomeada, ou a variante de permissao
// mudando) ficaria na tela ate o aviso fechar.
const toastSignature = (pending, manager) =>
  JSON.stringify([manager, pending.map(item => [item.id, item.name])]);

// Avisa o usuario quando uma conexao a que ELE tem acesso cai (admin: todas). O
// badge do menu lateral so aparece para quem gerencia conexoes, e o admin nem
// sempre entra no sistema todos os dias — aqui o atendente fica sabendo mesmo
// sem permissao.
//
// O aviso NAO se repete por tempo: some quando o usuario clica em OK ou no X,
// ou sozinho depois de 1min na tela — o que conta como lido, igual ao OK.
// Depois disso, so volta se houver mudanca real — uma conexao ainda nao
// dispensada cair, ou uma que caiu voltar ao ar e cair de novo.
const ConnectionAlerts = () => {
  const { user, menus } = useContext(AuthContext);
  const { whatsApps, loading } = useContext(WhatsAppsContext);

  // id -> { offlineSince, notified, name }. Ref e nao state porque isto nao
  // pinta nada: mudar nao pode causar render.
  const trackedRef = useRef(new Map());
  // Espelho em memoria do localStorage (id -> ackAt) + a chave que ele espelha.
  const ackRef = useRef({});
  const ackKeyRef = useRef(null);
  // Ids exibidos no toast aberto: define o que o OK/X vai dispensar.
  const shownRef = useRef([]);
  // Assinatura do texto exibido, para saber se ele precisa ser atualizado.
  const signatureRef = useRef("");
  const evaluateRef = useRef(() => {});
  const visibilityRef = useRef(() => {});
  // Contagem do fechamento automatico: quanto ainda falta e o timer em curso
  // (null = parada, seja por pausa ou por nao haver aviso aberto).
  const autoAckRef = useRef({ timer: null, remaining: 0, startedAt: 0 });

  // Funcoes abaixo so leem refs: podem ser chamadas de closures antigas
  // (timer, listener de visibilidade) sem ver estado desatualizado.
  const stopAutoAck = () => {
    const auto = autoAckRef.current;
    if (auto.timer) clearTimeout(auto.timer);
    auto.timer = null;
    auto.remaining = 0;
  };

  const runAutoAck = () => {
    const auto = autoAckRef.current;
    if (auto.timer || auto.remaining <= 0 || document.hidden) return;
    auto.startedAt = Date.now();
    auto.timer = setTimeout(() => {
      auto.timer = null;
      acknowledge();
    }, auto.remaining);
  };

  const pauseAutoAck = () => {
    const auto = autoAckRef.current;
    if (!auto.timer) return;
    clearTimeout(auto.timer);
    auto.timer = null;
    auto.remaining -= Date.now() - auto.startedAt;
  };

  // Conteudo novo na tela (inclusive conexao que acabou de entrar no aviso):
  // conta 1min de novo, porque o usuario ainda nao viu o que mudou.
  const startAutoAck = () => {
    stopAutoAck();
    autoAckRef.current.remaining = AUTO_ACK_MS;
    runAutoAck();
  };

  // Fecha o aviso SEM registrar dispensa (conexao voltou, troca de conta...).
  const closeToast = () => {
    stopAutoAck();
    shownRef.current = [];
    signatureRef.current = "";
    toast.dismiss(OFFLINE_TOAST_ID);
  };

  // Trocou de usuario/empresa: recarrega as dispensas dessa conta.
  const syncAckStorage = () => {
    const key = user?.id ? ackStorageKey(user) : null;
    if (key === ackKeyRef.current) return;
    ackKeyRef.current = key;
    ackRef.current = key ? readAck(key) : {};
    trackedRef.current = new Map();
    closeToast();
  };

  const persistAck = () => {
    if (ackKeyRef.current) writeAck(ackKeyRef.current, ackRef.current);
  };

  // Fecha o aviso e registra que o usuario viu as conexoes listadas nele —
  // pelo OK/X ou pelo fechamento automatico.
  const acknowledge = () => {
    const now = Date.now();
    shownRef.current.forEach(id => {
      ackRef.current[id] = now;
    });
    persistAck();
    closeToast();
  };

  const buildMessage = (pending, manager) => {
    const names = pending.map(item => item.name);
    const listed =
      names.slice(0, MAX_NAMES).join(", ") +
      (names.length > MAX_NAMES ? "..." : "");
    const single = pending.length === 1;
    const key = single
      ? manager
        ? "connections.alerts.offlineManager"
        : "connections.alerts.offline"
      : manager
      ? "connections.alerts.offlineMultipleManager"
      : "connections.alerts.offlineMultiple";

    return i18n.t(key, {
      name: names[0],
      names: listed,
      total: pending.length
    });
  };

  const renderContent = message => (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <span>{message}</span>
      <Button
        size="small"
        variant="outlined"
        onClick={acknowledge}
        style={{
          alignSelf: "flex-end",
          marginTop: 4,
          color: TOAST_TEXT_COLOR,
          borderColor: TOAST_TEXT_COLOR
        }}
      >
        {i18n.t("connections.alerts.ack")}
      </Button>
    </div>
  );

  // Substitui o X padrao do toastify para que fechar tambem grave a dispensa.
  // Cor inline (TOAST_TEXT_COLOR): estilo inline vence as classes do tema MUI.
  const renderCloseButton = () => (
    <IconButton
      size="small"
      onClick={acknowledge}
      aria-label={i18n.t("connections.alerts.dismiss")}
      style={{ alignSelf: "flex-start", padding: 2, color: TOAST_TEXT_COLOR }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );

  const syncToast = (pending, manager) => {
    // Ordem por id: a lista vem na ordem da API, e uma reordenacao ali nao pode
    // ser confundida com mudanca de conteudo.
    const ordered = [...pending].sort((a, b) => a.id - b.id);
    const ids = ordered.map(item => item.id);
    const signature = toastSignature(ordered, manager);
    const active = toast.isActive(OFFLINE_TOAST_ID);

    if (!ids.length) {
      closeToast();
      return;
    }

    if (active && signature === signatureRef.current) return;

    shownRef.current = ids;
    signatureRef.current = signature;
    startAutoAck();
    const content = renderContent(buildMessage(ordered, manager));

    if (active) {
      toast.update(OFFLINE_TOAST_ID, { render: content });
      return;
    }

    toast.warn(content, {
      toastId: OFFLINE_TOAST_ID,
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      closeButton: renderCloseButton
    });
  };

  const evaluate = () => {
    // Durante o carregamento inicial a lista chega vazia: nao e "tudo caido".
    if (loading || !user?.id) return;

    syncAckStorage();

    const relevant = getAlertWhatsApps(user, whatsApps);
    // Sem conexoes a acompanhar, um aviso aberto nao tem mais o que dizer: fecha
    // sem contar como lido. As dispensas guardadas ficam como estao — lista
    // vazia aqui pode ser transitoria e nao deve apagar o que o usuario ja
    // dispensou.
    if (!relevant.length) {
      closeToast();
      return;
    }

    const tracked = trackedRef.current;
    const ack = ackRef.current;
    const now = Date.now();
    const manager = canManageConnections(user, menus);
    const pending = [];
    let ackChanged = false;

    relevant.forEach(whats => {
      const id = Number(whats.id);
      const entry = tracked.get(id);

      if (isWhatsappOnline(whats)) {
        // Toast de recuperacao so para quem chegou a ser avisado — a dispensa
        // guardada prova que o aviso foi visto, mesmo em sessao anterior.
        if (entry?.notified || ack[id]) {
          toast.success(
            i18n.t("connections.alerts.recovered", { name: whats.name }),
            { toastId: onlineToastId(id), autoClose: AUTO_CLOSE_MS }
          );
        }
        // Voltar ao ar zera a dispensa: se cair de novo, isso e mudanca real e
        // o usuario deve ser avisado outra vez.
        if (ack[id]) {
          delete ack[id];
          ackChanged = true;
        }
        tracked.delete(id);
        return;
      }

      // OPENING / status desconhecido: neutro, preserva o que ja havia.
      if (!isWhatsappOffline(whats)) return;

      // Registrar e avaliar na MESMA passada: quem entra no sistema com a
      // conexao ja caida ha tempo e avisado de imediato, sem esperar o proximo
      // tick. `updatedAt` marca a ultima troca de status, entao a carencia ja
      // vem vencida nesse caso.
      let current = entry;
      if (!current) {
        const changedAt = Date.parse(whats.updatedAt);
        current = {
          offlineSince: Number.isFinite(changedAt)
            ? Math.min(changedAt, now)
            : now,
          notified: false,
          name: whats.name
        };
        tracked.set(id, current);
      }

      current.name = whats.name;
      if (now - current.offlineSince < OFFLINE_GRACE_MS) return;
      // Ja dispensada pelo usuario: fica fora do aviso ate voltar ao ar.
      if (ack[id]) return;

      current.notified = true;
      pending.push({ id, name: whats.name || `#${id}` });
    });

    // Conexoes que sairam da lista (deletadas / desatribuidas) nao ficam presas.
    const relevantIds = new Set(relevant.map(w => Number(w.id)));
    Array.from(tracked.keys()).forEach(id => {
      if (!relevantIds.has(id)) tracked.delete(id);
    });
    Object.keys(ack).forEach(key => {
      if (!relevantIds.has(Number(key))) {
        delete ack[key];
        ackChanged = true;
      }
    });

    if (ackChanged) persistAck();

    syncToast(pending, manager);
  };

  evaluateRef.current = evaluate;
  visibilityRef.current = () =>
    document.hidden ? pauseAutoAck() : runAutoAck();

  useEffect(() => {
    const timer = setInterval(() => evaluateRef.current(), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  // A contagem do fechamento automatico so corre com a aba visivel.
  useEffect(() => {
    const auto = autoAckRef.current;
    const onVisibility = () => visibilityRef.current();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimeout(auto.timer);
    };
  }, []);

  // Dispensar numa aba dispensa em todas — inclusive pelo fechamento
  // automatico. O evento `storage` so chega nas OUTRAS abas, que e exatamente o
  // caso: sem isto, quem trabalha com duas abas abertas clicava OK numa e a
  // outra seguia com o aviso na tela.
  useEffect(() => {
    const onStorage = event => {
      // event.key === null e localStorage.clear().
      if (event.key !== null && event.key !== ackKeyRef.current) return;
      if (ackKeyRef.current) ackRef.current = readAck(ackKeyRef.current);
      evaluateRef.current();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Reacao imediata a cada evento de status vindo do WhatsAppsContext.
  useEffect(() => {
    evaluateRef.current();
  }, [whatsApps, loading, user, menus]);

  return null;
};

export default ConnectionAlerts;
